import type { MissingEnrollmentSession } from "@/artifacts/admin/pages/Dashboard";
import { buildRows } from "@/artifacts/admin/utils/dashboard/buildMissingEnrollmentRows";
import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Input } from "@/components/ui/Input/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { numberInStringArrayFilter } from "@/utils/filters";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type SortingState,
  type ColumnDef,
  type ColumnFiltersState,
  getSortedRowModel,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Filter,
  UserRoundCog,
} from "lucide-react";
import { useMemo, useState } from "react";

export type MissingStudentRow = {
  sessionId: string;
  sessionName: string;
  studentId: string;
  studentName: string;
  studentGradeLevel: number;
};

const buildColumns = (
  onOpen: (studentId: string) => void,
): ColumnDef<MissingStudentRow>[] => [
  {
    accessorKey: "sessionName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Session{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: "arrIncludesSome",
  },
  {
    accessorKey: "studentName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Student{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "studentGradeLevel",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grade Level{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: numberInStringArrayFilter,
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon-small"
              onClick={() => onOpen(row.original.studentId)}
            >
              <UserRoundCog />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Manage {row.original.studentName}</TooltipContent>
        </Tooltip>
      </HStack>
    ),
    enableSorting: false,
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: MissingStudentRow[];
  columns: ColumnDef<MissingStudentRow>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  // Session Filtering

  const seshCol = table.getColumn("sessionName");
  const selectedSesh =
    (seshCol?.getFilterValue() as string[] | undefined) ?? [];

  const sessions = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.sessionName) set.add(d.sessionName);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleSesh = (sesh: string) => {
    const next = selectedSesh.includes(sesh)
      ? selectedSesh.filter((s) => s !== sesh)
      : [...selectedSesh, sesh];

    seshCol?.setFilterValue(next);
  };

  const clearSeshFilter = () => {
    seshCol?.setFilterValue([]);
  };

  // Grade Filtering

  const gradeCol = table.getColumn("studentGradeLevel");
  const selectedGrade =
    (gradeCol?.getFilterValue() as string[] | undefined) ?? [];

  const grades = useMemo(() => {
    const set = new Set<string>();
    for (const d of data)
      if (d.studentGradeLevel) set.add(String(d.studentGradeLevel));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleGrade = (grade: string) => {
    const next = selectedGrade.includes(grade)
      ? selectedGrade.filter((s) => s !== grade)
      : [...selectedGrade, grade];

    gradeCol?.setFilterValue(next);
  };

  const clearGradeFilter = () => {
    gradeCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Sessions</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {sessions.map((s) => (
                    <DropdownMenuCheckboxItem
                      key={s}
                      checked={selectedSesh.includes(s)}
                      onCheckedChange={() => toggleSesh(s)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {s}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearSeshFilter();
                    }}
                    disabled={selectedSesh.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {grades.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g}
                      checked={selectedGrade.includes(g)}
                      onCheckedChange={() => toggleGrade(g)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {g}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearGradeFilter();
                    }}
                    disabled={selectedGrade.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} students
          </p>
        </HStack>
        <div style={{ width: "240px", maxWidth: "100%" }}>
          <Input
            placeholder="Search students..."
            value={
              (table.getColumn("studentName")?.getFilterValue() as string) ?? ""
            }
            onChange={(e) =>
              table.getColumn("studentName")?.setFilterValue(e.target.value)
            }
          />
        </div>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

const MissingEnrollmentsTable = ({
  sessions,
  rotation,
  onOpen,
}: {
  sessions: MissingEnrollmentSession[];
  rotation: string;
  onOpen: (studentId: string) => void;
}) => {
  const { missingEnrollmentRows, missingPreferencesRows } = useMemo(
    () => buildRows(sessions),
    [sessions],
  );
  const columns = useMemo(() => buildColumns(onOpen), [onOpen]);

  return (
    <VStack gap="1rem">
      <h4
        style={{
          fontSize: "var(--text-md)",
          lineHeight: "var(--line-height-md)",
          fontWeight: "var(--font-bold)",
        }}
      >
        Missing Enrollments — {format(rotation, "MMMM dd, yyyy")}
      </h4>
      <DashboardCard>
        <DataTable data={missingEnrollmentRows} columns={columns} />
      </DashboardCard>
      <h4
        style={{
          fontSize: "var(--text-md)",
          lineHeight: "var(--line-height-md)",
          fontWeight: "var(--font-bold)",
        }}
      >
        Missing Preferences — {format(rotation, "MMMM dd, yyyy")}
      </h4>
      <DashboardCard>
        <DataTable data={missingPreferencesRows} columns={columns} />
      </DashboardCard>
    </VStack>
  );
};

export { MissingEnrollmentsTable };
